import {
  collection,
  deleteField,
  deleteDoc,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  startAfter,
  where,
  writeBatch,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { getFirebaseServices } from "./firebase";
import { accountAvatar, shouldSyncAccountAvatar } from "./terra-boa-profile-photos";
import {
  AgronomicGuide,
  CertificationSeal,
  initialListings,
  Listing,
  ListingModerationStatus,
  MarketQuote,
  ProductLeaflet,
  MediaImage,
  ModuleKey,
  ProfessionalProfileData,
  Profile,
  PromotionCampaign,
  PromotionPreference,
  profileRequestOptions,
  requiresAdminApproval,
  RoleKey,
  SavedItem,
  SponsoredStoreContent,
  SupplyStore,
  ProfileSealAssignment,
} from "./terra-boa-data";
import { BrazilLocation, BrazilMunicipality, findMunicipality, isValidBrazilLocation, loadBrazilMunicipalities, LocationCatalogEntry, locationFromParts } from "./terra-boa-locations";

export type TerraBoaSession = {
  mode: "local" | "firebase";
  profileId: string;
  uid?: string;
  email?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
  providerId?: string | null;
  isAdmin?: boolean;
  municipality?: string;
  municipalityCode?: string;
  state?: string;
  stateName?: string;
  commercialPlaceId?: string;
  commercialPlaceName?: string;
  commercialPlaceType?: BrazilLocation["commercialPlaceType"];
};

export class ListingSaveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ListingSaveError";
  }
}

const LISTINGS_KEY = "terra-boa:local:listings:v2";
const FAVORITES_PREFIX = "terra-boa:local:favorites:v2:";
const PROFILE_MEDIA_KEY = "terra-boa:local:profile-media:v2";
const ACCOUNT_LOCATION_KEY = "terra-boa:local:account-location:v1";
const SAVED_ITEMS_PREFIX = "terra-boa:local:saved-items:v1:";
const CERTIFICATION_SEALS_KEY = "terra-boa:local:certification-seals:v1";
const PROFILE_SEAL_ASSIGNMENTS_KEY = "terra-boa:local:profile-seal-assignments:v1";

export type AccountLocationRecord = BrazilLocation & {
  ownerUid: string;
  createdAt?: unknown;
  updatedAt?: unknown;
};

export type ProfileMediaRecord = {
  profileId: string;
  avatar: MediaImage;
  ownerUid?: string;
};

export type ProfileRequestRecord = {
  uid: string;
  email: string;
  requestedRole: RoleKey;
  requestedModule: ModuleKey;
  status: "pending" | "approved" | "rejected" | "revoked";
  requestedName?: string;
  whatsapp?: string;
  municipality?: string;
  municipalityCode?: string;
  state?: string;
  activityType?: string;
  representedStore?: string;
  representedStoreId?: string;
  businessHours?: string;
  address?: string;
  locationUrl?: string;
  branches?: ProfessionalProfileData["branches"];
  createdAt?: unknown;
  reviewedAt?: unknown;
  reviewedBy?: string;
  reviewedByEmail?: string;
  decisionReason?: string;
  decisionEventId?: string;
};

export type UserProfileRecord = Profile & {
  ownerUid: string;
  status: "active" | "revoked" | "blocked";
  municipality: string;
  municipalityCode: string;
  state: string;
  activityType: string;
  representedStore?: string;
  updatedAt?: unknown;
  revokedAt?: unknown;
  revokedBy?: string;
  revocationReason?: string;
  lastAuditEventId?: string;
};

export type AccountControlRecord = {
  uid: string;
  status: "active" | "blocked";
  previousProfileStatus?: "active" | "revoked";
  reason?: string;
  blockedAt?: unknown;
  blockedBy?: string;
  blockedByEmail?: string;
  unblockedAt?: unknown;
  unblockedBy?: string;
  unblockedByEmail?: string;
  lastEventId?: string;
  updatedAt?: unknown;
};

export type AccountLifecycleAction = "blocked" | "unblocked" | "deletion-requested" | "admin-deletion-requested" | "deleted" | "deletion-failed";

export type AccountLifecycleEventRecord = {
  id: string;
  targetUid: string;
  action: AccountLifecycleAction;
  reason: string;
  actorUid: string;
  actorEmail?: string;
  affectedListings?: number;
  createdAt?: unknown;
};

export type ProfileRequestDecision = "approved" | "rejected";

export type ProfileAuditAction = "approved" | "rejected" | "revoked";

export type ProfileAuditEventRecord = {
  id: string;
  targetUid: string;
  requestUid: string;
  requestedRole: "TECNICO";
  action: ProfileAuditAction;
  reason: string;
  actorUid: string;
  actorEmail: string;
  createdAt?: unknown;
};

export type ListingReportReason = "informacao-incorreta" | "conteudo-inadequado" | "suspeita-fraude" | "produto-indisponivel" | "outro";
export type ListingReportStatus = "pending" | "resolved";
export type ListingModerationAction = "hide" | "restore" | "remove";

export type ListingReportRecord = {
  id: string;
  listingId: string;
  listingTitle: string;
  listingModule: ModuleKey;
  listingOwnerId: string;
  reporterUid: string;
  reporterEmail: string;
  reason: ListingReportReason;
  details: string;
  status: ListingReportStatus;
  createdAt?: unknown;
  reviewedAt?: unknown;
  reviewedBy?: string;
  reviewedByEmail?: string;
  resolutionAction?: ListingModerationAction;
  resolutionReason?: string;
  moderationEventId?: string;
};

export type ListingModerationEventRecord = {
  id: string;
  listingId: string;
  reportId: string;
  action: ListingModerationAction;
  reason: string;
  actorUid: string;
  actorEmail: string;
  resultingStatus: ListingModerationStatus;
  createdAt?: unknown;
};

const MARKET_QUOTES_KEY = "terra-boa:local:market-quotes:v1";
const AGRONOMIC_GUIDES_KEY = "terra-boa:local:agronomic-guides:v1";
const PRODUCT_LEAFLETS_KEY = "terra-boa:local:product-leaflets:v1";
const LOCATION_CATALOG_KEY = "terra-boa:local:location-catalog:v1";
const SUPPLY_STORES_KEY = "terra-boa:local:supply-stores:v1";
const SPONSORED_CONTENT_KEY = "terra-boa:local:sponsored-store-content:v1";
const PROMOTION_CAMPAIGNS_KEY = "terra-boa:local:promotion-campaigns:v1";
const PROMOTION_PREFERENCES_PREFIX = "terra-boa:local:promotion-preferences:v1:";

function normalizeProfessionalData(details: ProfessionalProfileData): ProfessionalProfileData {
  const name = details.name.trim().replace(/\s+/g, " ").slice(0, 80);
  const whatsapp = details.whatsapp.replace(/\D/g, "").slice(0, 15);
  const municipality = details.municipality.trim().replace(/\s+/g, " ").slice(0, 60);
  const state = details.state.trim().toUpperCase().slice(0, 2);
  const municipalityRecord = findMunicipality(state, municipality);
  const municipalityCode = municipalityRecord?.id ?? details.municipalityCode.trim();
  const activityType = details.activityType.trim().replace(/\s+/g, " ").slice(0, 80);
  const representedStore = details.representedStore?.trim().replace(/\s+/g, " ").slice(0, 120) ?? "";
  const representedStoreId = details.representedStoreId?.trim().slice(0, 120) ?? "";
  const businessHours = details.businessHours?.trim().slice(0, 180) ?? "";
  const address = details.address?.trim().replace(/\s+/g, " ").slice(0, 180) ?? "";
  const locationUrl = normalizeLocationUrl(details.locationUrl);
  const branches = (details.branches ?? []).slice(0, 10).map((branch) => ({
    name: branch.name.trim().slice(0, 80),
    address: branch.address.trim().slice(0, 180),
    municipality: branch.municipality?.trim().slice(0, 80) ?? "",
    state: branch.state?.trim().toUpperCase().slice(0, 2) ?? "",
    whatsapp: branch.whatsapp?.replace(/\D/g, "").slice(0, 15) ?? "",
  }));
  if (name.length < 2) throw new Error("invalid-name");
  if (whatsapp.length < 10) throw new Error("invalid-whatsapp");
  if (municipality.length < 2 || (!municipalityRecord && !/^municipality-[a-z0-9-]{4,90}$/.test(municipalityCode)) || (municipalityRecord && municipalityRecord.id !== municipalityCode)) throw new Error("invalid-municipality");
  if (!/^[A-Z]{2}$/.test(state)) throw new Error("invalid-state");
  if (activityType.length < 2) throw new Error("invalid-activity");
  return { ...(details.presentation?.trim() ? { presentation: details.presentation.trim().slice(0, 180) } : {}), ...(details.segments ? { segments: [...new Set(details.segments)].filter((segment) => ["hortifruti", "graos", "pecuaria"].includes(segment)).slice(0, 3) } : {}), ...(details.commercialPlaceName?.trim() ? { commercialPlaceName: details.commercialPlaceName.trim().slice(0, 120) } : {}), ...(details.box?.trim() ? { box: details.box.trim().slice(0, 80) } : {}), name, whatsapp, municipality: municipalityRecord?.name ?? municipality, municipalityCode, state, activityType, representedStore, ...(representedStoreId ? { representedStoreId } : {}), ...(businessHours ? { businessHours } : {}), ...(address ? { address } : {}), ...(locationUrl ? { locationUrl } : {}), ...(branches.length ? { branches } : {}) };
}

function normalizeLocationUrl(value?: string) {
  const raw = value?.trim() ?? "";
  if (!raw) return "";
  let url: URL;
  try { url = new URL(raw); } catch { throw new Error("invalid-location-url"); }
  if (url.protocol !== "https:" || url.username || url.password || raw.length > 500) throw new Error("invalid-location-url");
  return url.toString();
}

function requestProfessionalData(request: ProfileRequestRecord): ProfessionalProfileData {
  return normalizeProfessionalData({
    name: request.requestedName || "",
    whatsapp: request.whatsapp || "",
    municipality: request.municipality || "",
    municipalityCode: request.municipalityCode || "",
    state: request.state || "",
    activityType: request.activityType || "",
    representedStore: request.representedStore || "",
    representedStoreId: request.representedStoreId || "",
    businessHours: request.businessHours || "",
    address: request.address || "",
    locationUrl: request.locationUrl || "",
    branches: request.branches || [],
  });
}

function buildUserProfile(uid: string, role: RoleKey, rawDetails: ProfessionalProfileData): UserProfileRecord {
  const option = profileRequestOptions.find((item) => item.role === role);
  if (!option) throw new Error("invalid-role");
  const details = normalizeProfessionalData(rawDetails);
  const safeName = details.name;
  const initials = safeName.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "TB";
  return {
    id: uid,
    ownerUid: uid,
    status: "active",
    name: safeName.slice(0, 80),
    role,
    roleLabel: option.label,
    ...(["DISTRIBUIDOR", "COOPERATIVA"].includes(role) ? { segments: details.segments ?? [] } : {}),
    ...(role === "DISTRIBUIDOR" ? { ...(details.commercialPlaceName ? { commercialPlaceName: details.commercialPlaceName } : {}), ...(details.box ? { box: details.box } : {}) } : {}),
    initials: initials.slice(0, 3),
    city: `${details.municipality}, ${details.state}`,
    phone: details.whatsapp,
    municipality: details.municipality,
    municipalityCode: details.municipalityCode,
    state: details.state,
    activityType: details.activityType,
    ...(role === "TECNICO" ? { representedStore: details.representedStore, ...(details.representedStoreId ? { representedStoreId: details.representedStoreId } : {}) } : {}),
    ...(role === "LOJA_INSUMOS" ? { ...(details.address ? { address: details.address } : {}), ...(details.locationUrl ? { locationUrl: details.locationUrl } : {}), businessHours: details.businessHours ?? "", branches: details.branches ?? [] } : {}),
    verified: false,
    since: String(new Date().getFullYear()),
    property: details.activityType,
    bio: ["DISTRIBUIDOR", "COOPERATIVA"].includes(role) ? details.presentation?.trim().slice(0, 180) || option.description : option.description,
    seals: [],
  };
}

function readLocalListings(): Listing[] {
  try {
    const stored = localStorage.getItem(LISTINGS_KEY);
    return stored ? (JSON.parse(stored) as Listing[]) : initialListings;
  } catch {
    return initialListings;
  }
}

function writeLocalListings(listings: Listing[]) {
  localStorage.setItem(LISTINGS_KEY, JSON.stringify(listings));
}

function readLocalMarketQuotes(): MarketQuote[] {
  try {
    return JSON.parse(localStorage.getItem(MARKET_QUOTES_KEY) ?? "[]") as MarketQuote[];
  } catch {
    return [];
  }
}

function writeLocalMarketQuotes(quotes: MarketQuote[]) {
  localStorage.setItem(MARKET_QUOTES_KEY, JSON.stringify(quotes));
}

function readLocalAgronomicGuides(): AgronomicGuide[] {
  try {
    return JSON.parse(localStorage.getItem(AGRONOMIC_GUIDES_KEY) ?? "[]") as AgronomicGuide[];
  } catch {
    return [];
  }
}

function writeLocalAgronomicGuides(guides: AgronomicGuide[]) {
  localStorage.setItem(AGRONOMIC_GUIDES_KEY, JSON.stringify(guides));
}

function readLocalProductLeaflets(): ProductLeaflet[] {
  try { return JSON.parse(localStorage.getItem(PRODUCT_LEAFLETS_KEY) ?? "[]") as ProductLeaflet[]; } catch { return []; }
}

function writeLocalProductLeaflets(leaflets: ProductLeaflet[]) { localStorage.setItem(PRODUCT_LEAFLETS_KEY, JSON.stringify(leaflets)); }

function readLocalLocationCatalog(): LocationCatalogEntry[] {
  try { return JSON.parse(localStorage.getItem(LOCATION_CATALOG_KEY) ?? "[]") as LocationCatalogEntry[]; } catch { return []; }
}

function writeLocalLocationCatalog(entries: LocationCatalogEntry[]) { localStorage.setItem(LOCATION_CATALOG_KEY, JSON.stringify(entries)); }

function readLocalSupplyStores(): SupplyStore[] {
  try { return JSON.parse(localStorage.getItem(SUPPLY_STORES_KEY) ?? "[]") as SupplyStore[]; } catch { return []; }
}

function writeLocalSupplyStores(stores: SupplyStore[]) { localStorage.setItem(SUPPLY_STORES_KEY, JSON.stringify(stores)); }

function readLocalSponsoredContent(): SponsoredStoreContent[] {
  try { return JSON.parse(localStorage.getItem(SPONSORED_CONTENT_KEY) ?? "[]") as SponsoredStoreContent[]; } catch { return []; }
}

function writeLocalSponsoredContent(items: SponsoredStoreContent[]) { localStorage.setItem(SPONSORED_CONTENT_KEY, JSON.stringify(items)); }

function readLocalPromotionCampaigns(): PromotionCampaign[] {
  try { return JSON.parse(localStorage.getItem(PROMOTION_CAMPAIGNS_KEY) ?? "[]") as PromotionCampaign[]; } catch { return []; }
}

function writeLocalPromotionCampaigns(items: PromotionCampaign[]) { localStorage.setItem(PROMOTION_CAMPAIGNS_KEY, JSON.stringify(items)); }

function promotionPreferencesKey(profileId: string) { return `${PROMOTION_PREFERENCES_PREFIX}${profileId}`; }

function readLocalPromotionPreferences(profileId: string): PromotionPreference[] {
  try { return JSON.parse(localStorage.getItem(promotionPreferencesKey(profileId)) ?? "[]") as PromotionPreference[]; } catch { return []; }
}

function writeLocalPromotionPreferences(profileId: string, items: PromotionPreference[]) { localStorage.setItem(promotionPreferencesKey(profileId), JSON.stringify(items)); }

function favoritesKey(profileId: string) {
  return `${FAVORITES_PREFIX}${profileId}`;
}

function readLocalFavorites(profileId: string): Set<string> {
  try {
    const stored = localStorage.getItem(favoritesKey(profileId));
    return new Set(stored ? (JSON.parse(stored) as string[]) : []);
  } catch {
    return new Set();
  }
}

function isFirebaseMode(session: TerraBoaSession) {
  return session.mode === "firebase" && Boolean(getFirebaseServices());
}

function requireFirebaseUser(session: TerraBoaSession): asserts session is TerraBoaSession & { uid: string } {
  if (session.mode !== "firebase" || !session.uid || !getFirebaseServices()) {
    throw new Error("auth-required");
  }
}

export const LISTING_PAGE_SIZE = 24;
export type ListingScope = { module?: ModuleKey; ownerId?: string; supplyStoreId?: string; onSale?: boolean };
export type ListingPagination = { hasMore: boolean; loading: boolean; loadMore: () => Promise<void> };

function mergeListingPages(primary: Listing[], additional: Listing[]) {
  const rows = new Map<string, Listing>();
  primary.forEach((listing) => rows.set(listing.id, listing));
  additional.forEach((listing) => rows.set(listing.id, listing));
  return [...rows.values()];
}

export async function fetchListingPage(session: TerraBoaSession, scope: ListingScope) {
  if (session.mode === "local") {
    return readLocalListings()
      .filter((listing) => (!scope.module || listing.module === scope.module) && (!scope.ownerId || listing.ownerId === scope.ownerId)
        && (!scope.supplyStoreId || listing.supplyStoreId === scope.supplyStoreId) && (scope.onSale === undefined || listing.onSale === scope.onSale))
      .sort((left, right) => right.id.localeCompare(left.id))
      .slice(0, LISTING_PAGE_SIZE);
  }
  const services = getFirebaseServices();
  if (!services) return [];
  const listingsCollection = collection(services.db, "listings");
  const scopedQuery = scope.supplyStoreId
    ? query(listingsCollection, where("supplyStoreId", "==", scope.supplyStoreId), ...(scope.onSale === undefined ? [] : [where("onSale", "==", scope.onSale)]))
    : scope.module
    ? query(listingsCollection, where("module", "==", scope.module))
    : scope.ownerId
      ? query(listingsCollection, where("ownerId", "==", scope.ownerId))
      : listingsCollection;
  const snapshot = await getDocs(query(scopedQuery, orderBy(documentId(), "desc"), limit(LISTING_PAGE_SIZE)));
  return snapshot.docs.map((item) => item.data() as Listing);
}

export async function fetchSharedListing(session: TerraBoaSession, listingId: string): Promise<Listing | null> {
  if (!/^[A-Za-z0-9_-]{4,128}$/.test(listingId)) return null;
  if (session.mode === "local") return readLocalListings().find((listing) => listing.id === listingId) ?? null;
  const services = getFirebaseServices();
  if (!services) return null;
  const snapshot = await getDoc(doc(services.db, "listings", listingId));
  return snapshot.exists() ? snapshot.data() as Listing : null;
}

export async function fetchSharedProfile(session: TerraBoaSession, profileId: string): Promise<UserProfileRecord | null> {
  if (!/^[A-Za-z0-9_-]{4,128}$/.test(profileId)) return null;
  if (session.mode === "local") return null;
  const services = getFirebaseServices();
  if (!services) throw new Error("firebase-unavailable");
  const snapshot = await getDoc(doc(services.db, "userProfiles", profileId));
  if (!snapshot.exists()) return null;
  const profile = snapshot.data() as UserProfileRecord;
  return profile.status === "active" ? profile : null;
}

export function subscribeListings(
  session: TerraBoaSession,
  onData: (listings: Listing[]) => void,
  onError: (message: string) => void,
  scope?: ListingScope,
  onPagination?: (pagination: ListingPagination) => void,
) {
  if (session.mode === "local") {
    const rows = readLocalListings().filter((listing) => (!scope?.module || listing.module === scope.module)
      && (!scope?.ownerId || listing.ownerId === scope.ownerId)).sort((left, right) => right.id.localeCompare(left.id));
    if (!scope?.module) {
      onData(rows);
      onPagination?.({ hasMore: false, loading: false, loadMore: async () => undefined });
      return () => undefined;
    }
    let visibleCount = LISTING_PAGE_SIZE;
    const publish = () => {
      onData(rows.slice(0, visibleCount));
      onPagination?.({
        hasMore: visibleCount < rows.length,
        loading: false,
        loadMore: async () => {
          visibleCount = Math.min(rows.length, visibleCount + LISTING_PAGE_SIZE);
          publish();
        },
      });
    };
    publish();
    return () => undefined;
  }

  const services = getFirebaseServices();
  if (!services) {
    onPagination?.({ hasMore: false, loading: false, loadMore: async () => undefined });
    onError("O Firebase não está disponível para carregar as publicações.");
    return () => undefined;
  }
  const listingsCollection = collection(services.db, "listings");
  const scopedSource = scope?.module
    ? query(listingsCollection, where("module", "==", scope.module))
    : scope?.ownerId
      ? query(listingsCollection, where("ownerId", "==", scope.ownerId))
      : listingsCollection;
  if (scope?.module) {
    const orderedSource = query(scopedSource, orderBy(documentId(), "desc"));
    let primaryRows: Listing[] = [];
    let additionalRows: Listing[] = [];
    let cursor: QueryDocumentSnapshot | null = null;
    let hasMore = true;
    let loading = true;
    let active = true;
    const publishPagination = (loadMore: () => Promise<void>) => onPagination?.({ hasMore, loading, loadMore });
    const loadMore = async () => {
      if (loading || !hasMore || !cursor) return;
      loading = true;
      publishPagination(loadMore);
      try {
        const snapshot = await getDocs(query(orderedSource, startAfter(cursor), limit(LISTING_PAGE_SIZE)));
        if (!active) return;
        additionalRows = mergeListingPages(additionalRows, snapshot.docs.map((item) => item.data() as Listing));
        cursor = snapshot.docs.at(-1) ?? cursor;
        hasMore = snapshot.size === LISTING_PAGE_SIZE;
        onData(mergeListingPages(primaryRows, additionalRows));
      } catch {
        if (!active) return;
        hasMore = false;
        onError("Não foi possível carregar mais publicações.");
      } finally {
        if (!active) return;
        loading = false;
        publishPagination(loadMore);
      }
    };
    const unsubscribe = onSnapshot(
      query(orderedSource, limit(LISTING_PAGE_SIZE)),
      (snapshot) => {
        primaryRows = snapshot.docs.map((item) => item.data() as Listing);
        if (!additionalRows.length) cursor = snapshot.docs.at(-1) ?? null;
        hasMore = additionalRows.length ? hasMore : snapshot.size === LISTING_PAGE_SIZE;
        loading = false;
        onData(mergeListingPages(primaryRows, additionalRows));
        publishPagination(loadMore);
      },
      () => {
        loading = false;
        hasMore = false;
        publishPagination(loadMore);
        onError("Não foi possível sincronizar as publicações com o Firebase.");
      },
    );
    return () => {
      active = false;
      unsubscribe();
    };
  }
  onPagination?.({ hasMore: false, loading: false, loadMore: async () => undefined });
  return onSnapshot(
    scopedSource,
    (snapshot) => onData(snapshot.docs.map((item) => item.data() as Listing)),
    () => onError("Não foi possível sincronizar as publicações com o Firebase."),
  );
}

export function subscribeMarketQuotes(
  session: TerraBoaSession,
  onData: (quotes: MarketQuote[]) => void,
  onError: (message: string) => void,
) {
  if (session.mode === "local") {
    onData(readLocalMarketQuotes());
    return () => undefined;
  }

  const services = getFirebaseServices();
  if (!services) {
    onError("O Firebase não está disponível para carregar as cotações.");
    return () => undefined;
  }
  return onSnapshot(
    collection(services.db, "marketQuotes"),
    (snapshot) => onData(snapshot.docs.map((item) => item.data() as MarketQuote)),
    () => onError("Não foi possível sincronizar as cotações."),
  );
}

export function subscribeAgronomicGuides(
  session: TerraBoaSession,
  onData: (guides: AgronomicGuide[]) => void,
  onError: (message: string) => void,
) {
  if (session.mode === "local") {
    onData(readLocalAgronomicGuides());
    return () => undefined;
  }

  const services = getFirebaseServices();
  if (!services) {
    onError("O Firebase não está disponível para carregar as indicações de uso.");
    return () => undefined;
  }
  return onSnapshot(
    collection(services.db, "agronomicGuides"),
    (snapshot) => onData(snapshot.docs.map((item) => item.data() as AgronomicGuide)),
    () => onError("Não foi possível sincronizar as indicações de uso."),
  );
}

export function subscribeProductLeaflets(session: TerraBoaSession, onData: (leaflets: ProductLeaflet[]) => void, onError: (message: string) => void) {
  if (session.mode === "local") { onData(readLocalProductLeaflets()); return () => undefined; }
  const services = getFirebaseServices();
  if (!services) { onError("O Firebase não está disponível para carregar o bulário."); return () => undefined; }
  return onSnapshot(collection(services.db, "productLeaflets"), (snapshot) => onData(snapshot.docs.map((item) => item.data() as ProductLeaflet)), () => onError("Não foi possível sincronizar o bulário."));
}

export function subscribeLocationCatalog(session: TerraBoaSession, onData: (entries: LocationCatalogEntry[]) => void, onError: (message: string) => void) {
  if (session.mode === "local") { onData(readLocalLocationCatalog()); return () => undefined; }
  const services = getFirebaseServices();
  if (!services) { onError("O Firebase não está disponível para carregar o catálogo de localizações."); return () => undefined; }
  return onSnapshot(collection(services.db, "locationCatalog"), (snapshot) => onData(snapshot.docs.map((item) => item.data() as LocationCatalogEntry)), () => onError("Não foi possível sincronizar o catálogo de localizações."));
}

export function subscribeSupplyStores(session: TerraBoaSession, onData: (stores: SupplyStore[]) => void, onError: (message: string) => void) {
  if (session.mode === "local") { onData(readLocalSupplyStores().filter((item) => item.profileStatus !== "revoked")); return () => undefined; }
  const services = getFirebaseServices();
  if (!services) { onError("O Firebase não está disponível para carregar as lojas de insumos."); return () => undefined; }
  return onSnapshot(collection(services.db, "supplyStores"), (snapshot) => onData(snapshot.docs.map((item) => item.data() as SupplyStore).filter((item) => item.profileStatus !== "revoked")), () => onError("Não foi possível sincronizar as lojas de insumos."));
}

export function subscribeSponsoredStoreContent(session: TerraBoaSession, storeId: string | null, includeDrafts: boolean, onData: (items: SponsoredStoreContent[]) => void, onError: (message: string) => void) {
  if (session.mode === "local") {
    onData(readLocalSponsoredContent().filter((item) => (!storeId || item.storeId === storeId) && (includeDrafts || item.published)));
    return () => undefined;
  }
  const services = getFirebaseServices();
  if (!services) { onError("Não foi possível carregar os conteúdos patrocinados."); return () => undefined; }
  const source = includeDrafts
    ? collection(services.db, "sponsoredStoreContent")
    : query(collection(services.db, "sponsoredStoreContent"), where("storeId", "==", storeId), where("published", "==", true));
  return onSnapshot(source, (snapshot) => onData(snapshot.docs.map((item) => item.data() as SponsoredStoreContent)), () => onError("Não foi possível sincronizar os conteúdos patrocinados."));
}

export function subscribePromotionCampaigns(session: TerraBoaSession, activeOnly: boolean, onData: (items: PromotionCampaign[]) => void, onError: (message: string) => void) {
  if (session.mode === "local") {
    onData(readLocalPromotionCampaigns().filter((item) => !activeOnly || item.active));
    return () => undefined;
  }
  const services = getFirebaseServices();
  if (!services) { onError("Não foi possível carregar as promoções."); return () => undefined; }
  const source = activeOnly ? query(collection(services.db, "promotionCampaigns"), where("active", "==", true)) : collection(services.db, "promotionCampaigns");
  return onSnapshot(source, (snapshot) => onData(snapshot.docs.map((item) => item.data() as PromotionCampaign)), () => onError("Não foi possível sincronizar as promoções."));
}

export function subscribeOwnStoreCampaigns(session: TerraBoaSession, onData: (items: PromotionCampaign[]) => void, onError: (message: string) => void) {
  if (session.mode === "local") { onData(readLocalPromotionCampaigns().filter((item) => item.ownerProfileId === session.profileId)); return () => undefined; }
  if (!session.uid) { onData([]); return () => undefined; }
  const services = getFirebaseServices();
  if (!services) return () => undefined;
  return onSnapshot(query(collection(services.db, "promotionCampaigns"), where("ownerProfileId", "==", session.uid)),
    (snapshot) => onData(snapshot.docs.map((item) => item.data() as PromotionCampaign)),
    () => onError("Não foi possível carregar as campanhas da loja."));
}

export function subscribePromotionPreferences(session: TerraBoaSession, onData: (items: PromotionPreference[]) => void, onError: (message: string) => void) {
  if (session.mode === "local") { onData(readLocalPromotionPreferences(session.profileId)); return () => undefined; }
  if (!session.uid) { onData([]); return () => undefined; }
  const services = getFirebaseServices();
  if (!services) { onError("Não foi possível carregar suas preferências de promoções."); return () => undefined; }
  return onSnapshot(collection(services.db, "users", session.uid, "promotionPreferences"), (snapshot) => onData(snapshot.docs.map((item) => item.data() as PromotionPreference)), () => onError("Não foi possível sincronizar suas preferências de promoções."));
}

export function subscribeFavorites(
  session: TerraBoaSession,
  profileId: string,
  onData: (favorites: Set<string>) => void,
  onError: (message: string) => void,
) {
  if (session.mode === "local") {
    onData(readLocalFavorites(profileId));
    return () => undefined;
  }

  if (!session.uid) {
    onData(new Set());
    return () => undefined;
  }

  const services = getFirebaseServices()!;
  return onSnapshot(
    collection(services.db, "users", session.uid, "favorites"),
    (snapshot) => onData(new Set(snapshot.docs.map((item) => item.id))),
    () => onError("Não foi possível sincronizar os favoritos."),
  );
}

export function subscribeSavedItems(
  session: TerraBoaSession,
  profileId: string,
  onData: (items: SavedItem[]) => void,
  onError: (message: string) => void,
) {
  if (session.mode === "local") {
    try { onData(JSON.parse(localStorage.getItem(`${SAVED_ITEMS_PREFIX}${profileId}`) ?? "[]") as SavedItem[]); }
    catch { onData([]); }
    return () => undefined;
  }
  if (!session.uid) { onData([]); return () => undefined; }
  const services = getFirebaseServices()!;
  return onSnapshot(
    collection(services.db, "users", session.uid, "savedItems"),
    (snapshot) => onData(snapshot.docs.map((item) => item.data() as SavedItem)),
    () => onError("Não foi possível sincronizar os favoritos de perfis e localizações."),
  );
}

export async function setSavedItemRecord(session: TerraBoaSession, profileId: string, item: SavedItem, favorite: boolean) {
  if (session.mode === "local") {
    const key = `${SAVED_ITEMS_PREFIX}${profileId}`;
    let current: SavedItem[] = [];
    try { current = JSON.parse(localStorage.getItem(key) ?? "[]") as SavedItem[]; } catch { current = []; }
    const next = favorite ? [item, ...current.filter((entry) => entry.id !== item.id)] : current.filter((entry) => entry.id !== item.id);
    localStorage.setItem(key, JSON.stringify(next));
    return;
  }
  requireFirebaseUser(session);
  const reference = doc(getFirebaseServices()!.db, "users", session.uid, "savedItems", item.id);
  const persistedItem = Object.fromEntries(Object.entries(item).filter(([, value]) => value !== undefined));
  if (favorite) await setDoc(reference, { ...persistedItem, createdAt: serverTimestamp() });
  else await deleteDoc(reference);
}

export function subscribeCertificationSeals(session: TerraBoaSession, onData: (items: CertificationSeal[]) => void, onError: (message: string) => void) {
  if (session.mode === "local") {
    try { onData(JSON.parse(localStorage.getItem(CERTIFICATION_SEALS_KEY) ?? "[]") as CertificationSeal[]); } catch { onData([]); }
    return () => undefined;
  }
  const services = getFirebaseServices();
  if (!services) { onError("Não foi possível carregar os selos."); return () => undefined; }
  return onSnapshot(collection(services.db, "certificationSeals"), (snapshot) => onData(snapshot.docs.map((item) => item.data() as CertificationSeal)), () => onError("Não foi possível sincronizar os selos."));
}

export function subscribeProfileSealAssignments(session: TerraBoaSession, onData: (items: ProfileSealAssignment[]) => void, onError: (message: string) => void) {
  if (session.mode === "local") {
    try { onData(JSON.parse(localStorage.getItem(PROFILE_SEAL_ASSIGNMENTS_KEY) ?? "[]") as ProfileSealAssignment[]); } catch { onData([]); }
    return () => undefined;
  }
  const services = getFirebaseServices();
  if (!services) { onError("Não foi possível carregar as atribuições de selos."); return () => undefined; }
  return onSnapshot(collection(services.db, "profileSealAssignments"), (snapshot) => onData(snapshot.docs.map((item) => item.data() as ProfileSealAssignment)), () => onError("Não foi possível sincronizar as atribuições de selos."));
}

export function subscribeProfileMedia(
  session: TerraBoaSession,
  onData: (media: Record<string, ProfileMediaRecord>) => void,
  onError: (message: string) => void,
  profileIds: string[],
) {
  if (session.mode === "local") {
    try {
      onData(JSON.parse(localStorage.getItem(PROFILE_MEDIA_KEY) ?? "{}") as Record<string, ProfileMediaRecord>);
    } catch {
      onData({});
    }
    return () => undefined;
  }

  const services = getFirebaseServices();
  if (!services || !isFirebaseMode(session)) {
    onError("O Firebase não está disponível para carregar as fotos dos perfis.");
    return () => undefined;
  }
  const ids = [...new Set(profileIds.filter(Boolean))].sort();
  if (!ids.length) { onData({}); return () => undefined; }
  if (!session.uid) {
    let cancelled = false;
    const records: Record<string, ProfileMediaRecord> = {};
    onData({});
    // Individual gets let rules verify each public profile without allowing collection scans.
    void (async () => {
      for (let offset = 0; offset < ids.length && !cancelled; offset += 10) {
        await Promise.all(ids.slice(offset, offset + 10).map(async (id) => {
          try {
            const snapshot = await getDoc(doc(services.db, "profileMedia", id));
            const record = snapshot.data() as ProfileMediaRecord | undefined;
            if (record?.profileId && record.avatar?.url) records[record.profileId] = record;
          } catch (error) {
            // Missing/private account photos are expected; preserve initials for those entries.
            if (!cancelled && (error as { code?: string }).code !== "permission-denied") {
              onError("Não foi possível carregar as fotos dos perfis.");
            }
          }
        }));
        if (!cancelled) onData({ ...records });
      }
    })();
    return () => { cancelled = true; };
  }
  const batches = new Map<number, Record<string, ProfileMediaRecord>>();
  const unsubscribers: Array<() => void> = [];
  for (let offset = 0; offset < ids.length; offset += 30) {
    const batchIndex = offset;
    unsubscribers.push(onSnapshot(
      query(collection(services.db, "profileMedia"), where(documentId(), "in", ids.slice(offset, offset + 30))),
      (snapshot) => {
        const records: Record<string, ProfileMediaRecord> = {};
        snapshot.docs.forEach((item) => {
          const record = item.data() as ProfileMediaRecord;
          if (record.profileId && record.avatar?.url) records[record.profileId] = record;
        });
        batches.set(batchIndex, records);
        if (batches.size === Math.ceil(ids.length / 30)) onData(Object.assign({}, ...batches.values()));
      },
      () => onError("Não foi possível carregar as fotos dos perfis."),
      ));
  }
  return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
}

export async function syncAccountAvatar(session: TerraBoaSession) {
  const incoming = accountAvatar(session.photoURL);
  if (session.mode !== "firebase" || !session.uid || !incoming) return;
  const services = getFirebaseServices();
  if (!services) return;
  const mediaRef = doc(services.db, "profileMedia", session.uid);
  await runTransaction(services.db, async (transaction) => {
    const snapshot = await transaction.get(mediaRef);
    if (!shouldSyncAccountAvatar(snapshot.data()?.avatar, incoming)) return;
    transaction.set(mediaRef, { profileId: session.uid, ownerUid: session.uid, avatar: incoming, updatedAt: serverTimestamp() });
  });
}

export function subscribeProfileRequest(
  session: TerraBoaSession,
  onData: (request: ProfileRequestRecord | null) => void,
  onError: (message: string) => void,
) {
  if (session.mode === "local" || !session.uid) {
    onData(null);
    return () => undefined;
  }

  const services = getFirebaseServices();
  if (!services) {
    onError("O Firebase não está disponível para consultar a solicitação de perfil.");
    return () => undefined;
  }
  return onSnapshot(
    doc(services.db, "profileRequests", session.uid),
    (snapshot) => onData(snapshot.exists() ? snapshot.data() as ProfileRequestRecord : null),
    () => onError("Não foi possível consultar a solicitação de perfil."),
  );
}

export function subscribeUserProfiles(
  session: TerraBoaSession,
  onData: (profiles: UserProfileRecord[]) => void,
  onError: (message: string) => void,
) {
  if (session.mode === "local") {
    onData([]);
    return () => undefined;
  }

  const services = getFirebaseServices();
  if (!services) {
    onError("O Firebase não está disponível para carregar os perfis.");
    return () => undefined;
  }
  const profilesQuery = session.isAdmin
    ? collection(services.db, "userProfiles")
    : query(collection(services.db, "userProfiles"), where("status", "==", "active"));
  return onSnapshot(
    profilesQuery,
    (snapshot) => onData(snapshot.docs.map((item) => item.data() as UserProfileRecord)),
    () => onError("Não foi possível carregar os perfis da comunidade."),
  );
}

export function subscribeAdminProfileRequests(
  session: TerraBoaSession,
  onData: (requests: ProfileRequestRecord[]) => void,
  onError: (message: string) => void,
) {
  if (session.mode === "local" || !session.uid || !session.isAdmin) {
    onData([]);
    return () => undefined;
  }

  const services = getFirebaseServices();
  if (!services) {
    onError("O Firebase não está disponível para carregar as solicitações.");
    return () => undefined;
  }
  return onSnapshot(
    collection(services.db, "profileRequests"),
    (snapshot) => onData(snapshot.docs.map((item) => item.data() as ProfileRequestRecord)),
    () => onError("Não foi possível carregar as solicitações administrativas."),
  );
}

export function subscribeAdminProfileAudit(
  session: TerraBoaSession,
  onData: (events: ProfileAuditEventRecord[]) => void,
  onError: (message: string) => void,
) {
  if (session.mode === "local" || !session.uid || !session.isAdmin) {
    onData([]);
    return () => undefined;
  }
  const services = getFirebaseServices();
  if (!services) {
    onError("O Firebase não está disponível para carregar o histórico de perfis.");
    return () => undefined;
  }
  return onSnapshot(
    collection(services.db, "profileAuditEvents"),
    (snapshot) => onData(snapshot.docs.map((item) => item.data() as ProfileAuditEventRecord)),
    () => onError("Não foi possível carregar o histórico administrativo de perfis."),
  );
}

export function subscribeAdminListingReports(
  session: TerraBoaSession,
  onData: (reports: ListingReportRecord[]) => void,
  onError: (message: string) => void,
) {
  if (session.mode === "local" || !session.uid || !session.isAdmin) {
    onData([]);
    return () => undefined;
  }
  const services = getFirebaseServices();
  if (!services) {
    onError("O Firebase não está disponível para carregar as denúncias.");
    return () => undefined;
  }
  return onSnapshot(
    collection(services.db, "listingReports"),
    (snapshot) => onData(snapshot.docs.map((item) => item.data() as ListingReportRecord)),
    () => onError("Não foi possível carregar a fila de denúncias."),
  );
}

export function subscribeAdminModerationAudit(
  session: TerraBoaSession,
  onData: (events: ListingModerationEventRecord[]) => void,
  onError: (message: string) => void,
) {
  if (session.mode === "local" || !session.uid || !session.isAdmin) {
    onData([]);
    return () => undefined;
  }
  const services = getFirebaseServices();
  if (!services) {
    onError("O Firebase não está disponível para carregar o histórico de moderação.");
    return () => undefined;
  }
  return onSnapshot(
    collection(services.db, "listingModerationEvents"),
    (snapshot) => onData(snapshot.docs.map((item) => item.data() as ListingModerationEventRecord)),
    () => onError("Não foi possível carregar o histórico de moderação."),
  );
}

export function subscribeAdminAccountControls(
  session: TerraBoaSession,
  onData: (records: AccountControlRecord[]) => void,
  onError: (message: string) => void,
) {
  if (session.mode === "local" || !session.uid || !session.isAdmin) {
    onData([]);
    return () => undefined;
  }
  const services = getFirebaseServices();
  if (!services) {
    onError("O Firebase não está disponível para carregar os bloqueios de conta.");
    return () => undefined;
  }
  return onSnapshot(
    collection(services.db, "accountControls"),
    (snapshot) => onData(snapshot.docs.map((item) => item.data() as AccountControlRecord)),
    () => onError("Não foi possível carregar os bloqueios de conta."),
  );
}

export function subscribeAdminAccountLifecycle(
  session: TerraBoaSession,
  onData: (records: AccountLifecycleEventRecord[]) => void,
  onError: (message: string) => void,
) {
  if (session.mode === "local" || !session.uid || !session.isAdmin) {
    onData([]);
    return () => undefined;
  }
  const services = getFirebaseServices();
  if (!services) {
    onError("O Firebase não está disponível para carregar o histórico de contas.");
    return () => undefined;
  }
  return onSnapshot(
    collection(services.db, "accountLifecycleEvents"),
    (snapshot) => onData(snapshot.docs.map((item) => item.data() as AccountLifecycleEventRecord)),
    () => onError("Não foi possível carregar o histórico de contas."),
  );
}

export async function setAccountBlocked(
  session: TerraBoaSession,
  targetUid: string,
  blocked: boolean,
  rawReason: string,
) {
  requireFirebaseUser(session);
  if (!session.isAdmin) throw new Error("admin-required");
  const reason = rawReason.trim().replace(/\s+/g, " ").slice(0, 300);
  if (reason.length < 5) throw new Error("block-reason-required");
  const services = getFirebaseServices()!;
  const callable = httpsCallable<
    { targetUid: string; blocked: boolean; reason: string },
    { status: "active" | "blocked"; affectedListings: number; eventId: string }
  >(services.functions, "setTerraBoaAccountBlock");
  return (await callable({ targetUid, blocked, reason })).data;
}

export async function deleteStoreAccount(
  session: TerraBoaSession,
  targetUid: string,
  rawReason: string,
) {
  requireFirebaseUser(session);
  if (!session.isAdmin) throw new Error("admin-required");
  const reason = rawReason.trim().replace(/\s+/g, " ").slice(0, 300);
  if (reason.length < 5) throw new Error("deletion-reason-required");
  const callable = httpsCallable<
    { targetUid: string; reason: string },
    { deleted: true; affectedListings: number; eventId: string }
  >(getFirebaseServices()!.functions, "deleteTerraBoaStoreAccountByAdmin");
  return (await callable({ targetUid, reason })).data;
}

export async function activateSelfManagedProfile(session: TerraBoaSession, role: RoleKey, details: ProfessionalProfileData) {
  requireFirebaseUser(session);
  if (requiresAdminApproval(role)) throw new Error("admin-approval-required");

  const services = getFirebaseServices()!;
  const profile = buildUserProfile(session.uid, role, details);

  const profileRef = doc(services.db, "userProfiles", session.uid);
  const requestRef = doc(services.db, "profileRequests", session.uid);
  const [requestSnapshot, existingProfile] = await Promise.all([getDoc(requestRef), getDoc(profileRef)]);
  if (existingProfile.exists()) profile.since = existingProfile.data().since || profile.since;
  const batch = writeBatch(services.db);
  batch.set(profileRef, { ...profile, updatedAt: serverTimestamp() });
  if (requestSnapshot.exists() && requestSnapshot.data().status === "pending") batch.delete(requestRef);
  await batch.commit();
  return profile;
}

export async function reviewTechnicalProfileRequest(
  session: TerraBoaSession,
  request: ProfileRequestRecord,
  decision: ProfileRequestDecision,
  rawReason: string,
) {
  requireFirebaseUser(session);
  if (!session.isAdmin) throw new Error("admin-required");
  if (request.requestedRole === "LOJA_INSUMOS") {
    const callable = httpsCallable(getFirebaseServices()!.functions, "reviewTerraBoaStoreProfile");
    await callable({ targetUid: request.uid, decision, reason: rawReason });
    return;
  }
  const services = getFirebaseServices()!;
  const requestRef = doc(services.db, "profileRequests", request.uid);
  const profileRef = doc(services.db, "userProfiles", request.uid);
  const eventRef = doc(collection(services.db, "profileAuditEvents"));
  const reason = rawReason.trim().replace(/\s+/g, " ").slice(0, 300);
  if (decision === "rejected" && reason.length < 5) throw new Error("rejection-reason-required");
  const recordedReason = decision === "approved" ? "Perfil profissional aprovado pela administração." : reason;

  // A validação do perfil usa a referência oficial do município. Ela é carregada
  // sob demanda para não pesar na inicialização, inclusive na área administrativa.
  if (decision === "approved") await loadBrazilMunicipalities();

  await runTransaction(services.db, async (transaction) => {
    const snapshot = await transaction.get(requestRef);
    if (!snapshot.exists()) throw new Error("request-not-found");
    const current = snapshot.data() as ProfileRequestRecord;
    if (current.status !== "pending" || !["TECNICO", "LOJA_INSUMOS"].includes(current.requestedRole)) throw new Error("request-already-reviewed");

    if (decision === "approved") {
      const profile = buildUserProfile(current.uid, current.requestedRole, requestProfessionalData(current));
      transaction.set(profileRef, { ...profile, lastAuditEventId: eventRef.id, updatedAt: serverTimestamp() });
    }
    transaction.update(requestRef, {
      status: decision,
      reviewedAt: serverTimestamp(),
      reviewedBy: session.uid,
      reviewedByEmail: session.email ?? "administracao@terraboa",
      decisionReason: recordedReason,
      decisionEventId: eventRef.id,
    });
    transaction.set(eventRef, {
      id: eventRef.id,
      targetUid: current.uid,
      requestUid: current.uid,
      requestedRole: current.requestedRole,
      action: decision,
      reason: recordedReason,
      actorUid: session.uid,
      actorEmail: session.email ?? "administracao@terraboa",
      createdAt: serverTimestamp(),
    });
  });
}

export async function revokeTechnicalProfile(
  session: TerraBoaSession,
  request: ProfileRequestRecord,
  rawReason: string,
) {
  requireFirebaseUser(session);
  if (!session.isAdmin) throw new Error("admin-required");
  if (request.requestedRole === "LOJA_INSUMOS") {
    const callable = httpsCallable(getFirebaseServices()!.functions, "reviewTerraBoaStoreProfile");
    await callable({ targetUid: request.uid, decision: "revoked", reason: rawReason });
    return;
  }
  const reason = rawReason.trim().replace(/\s+/g, " ").slice(0, 300);
  if (reason.length < 5) throw new Error("revocation-reason-required");
  const services = getFirebaseServices()!;
  const requestRef = doc(services.db, "profileRequests", request.uid);
  const profileRef = doc(services.db, "userProfiles", request.uid);
  const eventRef = doc(collection(services.db, "profileAuditEvents"));

  await runTransaction(services.db, async (transaction) => {
    const [requestSnapshot, profileSnapshot] = await Promise.all([
      transaction.get(requestRef),
      transaction.get(profileRef),
    ]);
    if (!requestSnapshot.exists() || !profileSnapshot.exists()) throw new Error("profile-not-found");
    const currentRequest = requestSnapshot.data() as ProfileRequestRecord;
    const currentProfile = profileSnapshot.data() as UserProfileRecord;
    if (currentRequest.status !== "approved" || currentProfile.status !== "active" || !["TECNICO", "LOJA_INSUMOS"].includes(currentProfile.role)) {
      throw new Error("profile-not-active");
    }
    transaction.update(profileRef, {
      status: "revoked",
      revokedAt: serverTimestamp(),
      revokedBy: session.uid,
      revocationReason: reason,
      lastAuditEventId: eventRef.id,
      updatedAt: serverTimestamp(),
    });
    transaction.update(requestRef, {
      status: "revoked",
      reviewedAt: serverTimestamp(),
      reviewedBy: session.uid,
      reviewedByEmail: session.email ?? "administracao@terraboa",
      decisionReason: reason,
      decisionEventId: eventRef.id,
    });
    transaction.set(eventRef, {
      id: eventRef.id,
      targetUid: currentRequest.uid,
      requestUid: currentRequest.uid,
      requestedRole: currentRequest.requestedRole,
      action: "revoked",
      reason,
      actorUid: session.uid,
      actorEmail: session.email ?? "administracao@terraboa",
      createdAt: serverTimestamp(),
    });
  });
}

export async function createProfileRequest(
  session: TerraBoaSession,
  requestedRole: RoleKey,
  requestedModule: ModuleKey,
  rawDetails: ProfessionalProfileData,
) {
  requireFirebaseUser(session);
  if (!session.email) throw new Error("email-required");
  if (!requiresAdminApproval(requestedRole)) throw new Error("self-service-profile");
  const services = getFirebaseServices()!;
  const details = normalizeProfessionalData(rawDetails);
  await setDoc(doc(services.db, "profileRequests", session.uid), {
    uid: session.uid,
    email: session.email,
    requestedRole,
    requestedModule,
    requestedName: details.name,
    whatsapp: details.whatsapp,
    municipality: details.municipality,
    municipalityCode: details.municipalityCode,
    state: details.state,
    activityType: details.activityType,
    representedStore: details.representedStore ?? "",
    ...(details.representedStoreId ? { representedStoreId: details.representedStoreId } : {}),
    ...(details.businessHours ? { businessHours: details.businessHours } : {}),
    ...(details.address ? { address: details.address } : {}),
    ...(details.locationUrl ? { locationUrl: details.locationUrl } : {}),
    ...(details.branches?.length ? { branches: details.branches } : {}),
    status: "pending",
    createdAt: serverTimestamp(),
  });
}

export async function createListingReport(
  session: TerraBoaSession,
  listing: Listing,
  reason: ListingReportReason,
  rawDetails: string,
) {
  requireFirebaseUser(session);
  if (listing.ownerId === session.profileId || listing.ownerId === session.uid) throw new Error("own-listing-report");
  if (!session.email) throw new Error("email-required");
  const details = rawDetails.trim().replace(/\s+/g, " ").slice(0, 500);
  if (reason === "outro" && details.length < 5) throw new Error("report-details-required");
  const services = getFirebaseServices()!;
  const reportId = `${listing.id}_${session.uid}`;
  await setDoc(doc(services.db, "listingReports", reportId), {
    id: reportId,
    listingId: listing.id,
    listingTitle: listing.title.slice(0, 120),
    listingModule: listing.module,
    listingOwnerId: listing.ownerId,
    reporterUid: session.uid,
    reporterEmail: session.email,
    reason,
    details,
    status: "pending",
    createdAt: serverTimestamp(),
  });
}

export async function moderateListing(
  session: TerraBoaSession,
  report: ListingReportRecord,
  action: ListingModerationAction,
  rawReason: string,
) {
  requireFirebaseUser(session);
  if (!session.isAdmin) throw new Error("admin-required");
  const reason = rawReason.trim().replace(/\s+/g, " ").slice(0, 300);
  if (reason.length < 5) throw new Error("moderation-reason-required");
  const services = getFirebaseServices()!;
  const callable = httpsCallable<
    { reportId: string; action: ListingModerationAction; reason: string },
    { moderated: true; eventId: string; resultingStatus: ListingModerationStatus }
  >(services.functions, "moderateTerraBoaListing");
  return (await callable({ reportId: report.id, action, reason })).data;
}

export async function saveProfileMediaRecord(session: TerraBoaSession, profileId: string, avatar: MediaImage) {
  if (session.mode === "local") {
    let current: Record<string, ProfileMediaRecord> = {};
    try {
      current = JSON.parse(localStorage.getItem(PROFILE_MEDIA_KEY) ?? "{}") as Record<string, ProfileMediaRecord>;
    } catch {
      current = {};
    }
    current[profileId] = { profileId, avatar };
    localStorage.setItem(PROFILE_MEDIA_KEY, JSON.stringify(current));
    return;
  }

  requireFirebaseUser(session);
  const services = getFirebaseServices()!;
  await setDoc(doc(services.db, "profileMedia", profileId), {
    profileId,
    ownerUid: session.uid,
    avatar,
    updatedAt: serverTimestamp(),
  });
}

export async function saveListingRecord(session: TerraBoaSession, listing: Listing) {
  if (session.mode === "local") {
    const current = readLocalListings();
    const exists = current.some((item) => item.id === listing.id);
    writeLocalListings(exists ? current.map((item) => (item.id === listing.id ? listing : item)) : [listing, ...current]);
    return;
  }

  requireFirebaseUser(session);
  const services = getFirebaseServices()!;
  try {
    const saveListing = httpsCallable<{ listing: Listing }, { saved: boolean; listingId: string }>(services.functions, "saveTerraBoaListing");
    await saveListing({ listing });
  } catch (error) {
    const code = error instanceof Error && "code" in error ? String(error.code) : "";
    if (code.includes("unauthenticated")) throw new ListingSaveError("Sua sessão expirou. Entre novamente e tente publicar.");
    if (code.includes("permission-denied")) throw new ListingSaveError("Seu perfil, a loja vinculada ou as imagens não possuem permissão para salvar a publicação.");
    if (code.includes("invalid-argument") || code.includes("failed-precondition")) {
      throw new ListingSaveError(error instanceof Error ? error.message.replace(/^Firebase:\s*/i, "") : "Revise os dados e as imagens da publicação.");
    }
    if (code.includes("unavailable") || code.includes("deadline-exceeded")) throw new ListingSaveError("A conexão com o serviço de publicação falhou. Tente novamente.");
    throw new ListingSaveError("Não foi possível concluir a gravação da publicação.");
  }
}

export function localAccountLocation() {
  try {
    const stored = localStorage.getItem(ACCOUNT_LOCATION_KEY);
    return stored ? locationFromParts(JSON.parse(stored) as Partial<BrazilLocation>) : null;
  } catch {
    return null;
  }
}

export async function saveAccountLocation(session: TerraBoaSession, rawLocation: BrazilLocation, additionalMunicipalities: BrazilMunicipality[] = []) {
  const location = locationFromParts(rawLocation, additionalMunicipalities);
  if (!isValidBrazilLocation(location, additionalMunicipalities)) throw new Error("invalid-location");
  if (session.mode === "local") {
    localStorage.setItem(ACCOUNT_LOCATION_KEY, JSON.stringify(location));
    return location;
  }

  requireFirebaseUser(session);
  const services = getFirebaseServices()!;
  const preferenceRef = doc(services.db, "accountPreferences", session.uid);
  const current = await getDoc(preferenceRef);
  await setDoc(preferenceRef, {
    ownerUid: session.uid,
    ...location,
    ...(current.exists() ? {} : { createdAt: serverTimestamp() }),
    updatedAt: serverTimestamp(),
  }, { merge: current.exists() });
  return location;
}

export async function saveMarketQuote(session: TerraBoaSession, quote: MarketQuote) {
  if (!session.isAdmin) throw new Error("admin-required");
  if (session.mode === "local") {
    const quotes = readLocalMarketQuotes();
    const next = { ...quote, updatedAt: new Date(), createdAt: quote.createdAt ?? new Date() };
    writeLocalMarketQuotes(quotes.some((item) => item.id === quote.id)
      ? quotes.map((item) => item.id === quote.id ? next : item)
      : [...quotes, next]);
    return;
  }

  requireFirebaseUser(session);
  const services = getFirebaseServices()!;
  const existing = Boolean(quote.createdAt);
  await setDoc(doc(services.db, "marketQuotes", quote.id), {
    ...quote,
    ...(existing ? {} : { createdAt: serverTimestamp() }),
    updatedAt: serverTimestamp(),
  }, { merge: existing });
}

export async function deleteMarketQuote(session: TerraBoaSession, quoteId: string) {
  if (!session.isAdmin) throw new Error("admin-required");
  if (session.mode === "local") {
    writeLocalMarketQuotes(readLocalMarketQuotes().filter((item) => item.id !== quoteId));
    return;
  }

  requireFirebaseUser(session);
  await deleteDoc(doc(getFirebaseServices()!.db, "marketQuotes", quoteId));
}

export async function saveAgronomicGuide(session: TerraBoaSession, guide: AgronomicGuide) {
  if (!session.isAdmin) throw new Error("admin-required");
  if (session.mode === "local") {
    const guides = readLocalAgronomicGuides();
    const next = { ...guide, updatedAt: new Date(), createdAt: guide.createdAt ?? new Date() };
    writeLocalAgronomicGuides(guides.some((item) => item.id === guide.id)
      ? guides.map((item) => item.id === guide.id ? next : item)
      : [...guides, next]);
    return;
  }
  requireFirebaseUser(session);
  const existing = Boolean(guide.createdAt);
  await setDoc(doc(getFirebaseServices()!.db, "agronomicGuides", guide.id), {
    ...guide,
    ...(existing ? {} : { createdAt: serverTimestamp() }),
    updatedAt: serverTimestamp(),
  }, { merge: existing });
}

export async function deleteAgronomicGuide(session: TerraBoaSession, guideId: string) {
  if (!session.isAdmin) throw new Error("admin-required");
  if (session.mode === "local") {
    writeLocalAgronomicGuides(readLocalAgronomicGuides().filter((item) => item.id !== guideId));
    return;
  }
  requireFirebaseUser(session);
  await deleteDoc(doc(getFirebaseServices()!.db, "agronomicGuides", guideId));
}

export async function saveProductLeaflet(session: TerraBoaSession, leaflet: ProductLeaflet) {
  if (!session.isAdmin) throw new Error("admin-required");
  if (session.mode === "local") { const items = readLocalProductLeaflets(); const next = { ...leaflet, updatedAt: new Date(), createdAt: leaflet.createdAt ?? new Date() }; writeLocalProductLeaflets(items.some((item) => item.id === leaflet.id) ? items.map((item) => item.id === leaflet.id ? next : item) : [...items, next]); return; }
  requireFirebaseUser(session); const existing = Boolean(leaflet.createdAt);
  await setDoc(doc(getFirebaseServices()!.db, "productLeaflets", leaflet.id), { ...leaflet, ...(existing ? {} : { createdAt: serverTimestamp() }), updatedAt: serverTimestamp() }, { merge: existing });
}

export async function deleteProductLeaflet(session: TerraBoaSession, leafletId: string) {
  if (!session.isAdmin) throw new Error("admin-required");
  if (session.mode === "local") { writeLocalProductLeaflets(readLocalProductLeaflets().filter((item) => item.id !== leafletId)); return; }
  requireFirebaseUser(session); await deleteDoc(doc(getFirebaseServices()!.db, "productLeaflets", leafletId));
}

export async function saveLocationCatalogEntry(session: TerraBoaSession, entry: LocationCatalogEntry) {
  if (!session.isAdmin) throw new Error("admin-required");
  const normalized: LocationCatalogEntry = {
    ...entry,
    id: entry.id.trim().slice(0, 100),
    name: entry.name.trim().replace(/\s+/g, " ").slice(0, 120),
    state: entry.state.trim().toUpperCase().slice(0, 2),
    stateName: entry.stateName.trim().replace(/\s+/g, " ").slice(0, 40),
    municipality: entry.municipality.trim().replace(/\s+/g, " ").slice(0, 80),
    municipalityCode: entry.municipalityCode.trim().slice(0, 100),
    ...(Number.isFinite(entry.latitude) && Number.isFinite(entry.longitude) ? { latitude: Number(entry.latitude), longitude: Number(entry.longitude) } : {}),
    sourceReference: entry.sourceReference.trim().slice(0, 300),
  };
  if (!normalized.id || !normalized.name || !normalized.municipality || !normalized.municipalityCode || !/^https:\/\//.test(normalized.sourceReference)) throw new Error("invalid-location-catalog-entry");
  if ((normalized.latitude !== undefined || normalized.longitude !== undefined) && (!Number.isFinite(normalized.latitude) || !Number.isFinite(normalized.longitude) || normalized.latitude! < -34 || normalized.latitude! > 6 || normalized.longitude! < -74 || normalized.longitude! > -28)) throw new Error("invalid-location-coordinates");
  if (normalized.kind === "commercial-place" && !["ceasa", "feira"].includes(normalized.placeType ?? "")) throw new Error("invalid-commercial-place");
  if (session.mode === "local") {
    const entries = readLocalLocationCatalog();
    const next = { ...normalized, createdAt: entry.createdAt ?? new Date(), updatedAt: new Date() };
    writeLocalLocationCatalog(entries.some((item) => item.id === normalized.id) ? entries.map((item) => item.id === normalized.id ? next : item) : [...entries, next]);
    return;
  }
  requireFirebaseUser(session);
  const entryRef = doc(getFirebaseServices()!.db, "locationCatalog", normalized.id);
  const existing = Boolean(entry.createdAt);
  await setDoc(entryRef, { ...normalized, ...(existing ? {} : { createdAt: serverTimestamp() }), updatedAt: serverTimestamp() }, { merge: existing });
}

export async function deleteLocationCatalogEntry(session: TerraBoaSession, entryId: string) {
  if (!session.isAdmin) throw new Error("admin-required");
  if (session.mode === "local") { writeLocalLocationCatalog(readLocalLocationCatalog().filter((item) => item.id !== entryId)); return; }
  requireFirebaseUser(session);
  await deleteDoc(doc(getFirebaseServices()!.db, "locationCatalog", entryId));
}

export async function saveSupplyStore(session: TerraBoaSession, store: SupplyStore) {
  if (!session.isAdmin) throw new Error("admin-required");
  const normalized: SupplyStore = {
    id: store.id.trim().slice(0, 100),
    name: store.name.trim().replace(/\s+/g, " ").slice(0, 120),
    ...(store.municipality?.trim() ? { municipality: store.municipality.trim().replace(/\s+/g, " ").slice(0, 80) } : {}),
    ...(store.state?.trim() ? { state: store.state.trim().toUpperCase().slice(0, 2) } : {}),
    ...(store.whatsapp?.replace(/\D/g, "") ? { whatsapp: store.whatsapp.replace(/\D/g, "").slice(0, 15) } : {}),
    ...(store.address?.trim() ? { address: store.address.trim().replace(/\s+/g, " ").slice(0, 180) } : {}),
    ...(store.businessHours?.trim() ? { businessHours: store.businessHours.trim().replace(/\s+/g, " ").slice(0, 180) } : {}),
    ...(store.locationUrl?.trim() ? { locationUrl: normalizeLocationUrl(store.locationUrl) } : {}),
    ...(store.profileId ? { profileId: store.profileId, ownerUid: store.ownerUid } : {}),
    ...(store.branches ? { branches: store.branches } : {}),
    ...(store.advertising ? { advertising: {
      allowOptIn: store.advertising.allowOptIn === true, allowPopup: store.advertising.allowPopup === true,
      optInLimit: Math.min(20, Math.max(0, Math.floor(store.advertising.optInLimit || 0))),
      popupLimit: Math.min(20, Math.max(0, Math.floor(store.advertising.popupLimit || 0))),
    } } : {}),
    ...(store.sponsorSlot ? { sponsorSlot: store.sponsorSlot } : {}),
    showInQuickFilter: store.showInQuickFilter === true,
    ...(store.showOnLoading !== undefined ? { showOnLoading: store.showOnLoading === true } : {}),
  };
  if (!normalized.id || normalized.name.length < 2 || (normalized.state && !/^(AC|AL|AP|AM|BA|CE|DF|ES|GO|MA|MT|MS|MG|PA|PB|PR|PE|PI|RJ|RN|RS|RO|RR|SC|SP|SE|TO)$/.test(normalized.state)) || (normalized.whatsapp && !/^\d{10,15}$/.test(normalized.whatsapp)) || (normalized.sponsorSlot && !["vegetal", "loja-02", "loja-03"].includes(normalized.sponsorSlot))) throw new Error("invalid-supply-store");
  if (session.mode === "local") {
    const stores = readLocalSupplyStores();
    const next = { ...normalized, createdAt: store.createdAt ?? new Date(), updatedAt: new Date() };
    writeLocalSupplyStores(stores.some((item) => item.id === normalized.id) ? stores.map((item) => item.id === normalized.id ? next : item) : [...stores, next]);
    return;
  }
  requireFirebaseUser(session);
  const existing = Boolean(store.createdAt);
  const storeRef = doc(getFirebaseServices()!.db, "supplyStores", normalized.id);
  if (existing) {
    await setDoc(storeRef, {
      ...normalized,
      municipality: normalized.municipality ?? deleteField(),
      state: normalized.state ?? deleteField(),
      whatsapp: normalized.whatsapp ?? deleteField(),
      address: normalized.address ?? deleteField(),
      businessHours: normalized.businessHours ?? deleteField(),
      locationUrl: normalized.locationUrl ?? deleteField(),
      sponsorSlot: normalized.sponsorSlot ?? deleteField(),
      showInQuickFilter: normalized.showInQuickFilter === true,
      ...(store.advertising ? { advertising: normalized.advertising } : {}),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    return;
  }
  await setDoc(storeRef, { ...normalized, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
}

export async function deleteSupplyStore(session: TerraBoaSession, storeId: string) {
  if (!session.isAdmin) throw new Error("admin-required");
  if (session.mode === "local") { writeLocalSupplyStores(readLocalSupplyStores().filter((item) => item.id !== storeId)); return; }
  requireFirebaseUser(session);
  await deleteDoc(doc(getFirebaseServices()!.db, "supplyStores", storeId));
}

function normalizedSponsoredContent(item: SponsoredStoreContent): SponsoredStoreContent {
  return {
    ...item,
    id: item.id.trim().slice(0, 100),
    storeId: item.storeId.trim().slice(0, 100),
    title: item.title.trim().replace(/\s+/g, " ").slice(0, 120),
    summary: item.summary.trim().replace(/\s+/g, " ").slice(0, 240),
    body: item.body.trim().slice(0, 3000),
    author: item.author.trim().replace(/\s+/g, " ").slice(0, 100),
    category: item.category.trim().replace(/\s+/g, " ").slice(0, 80),
    published: item.published === true,
  };
}

export async function saveSponsoredStoreContent(session: TerraBoaSession, item: SponsoredStoreContent) {
  if (!session.isAdmin) throw new Error("admin-required");
  const normalized = normalizedSponsoredContent(item);
  if (!normalized.id || !normalized.storeId || normalized.title.length < 3 || normalized.summary.length < 5 || normalized.body.length < 20 || normalized.author.length < 2 || normalized.category.length < 2) throw new Error("invalid-sponsored-content");
  if (session.mode === "local") {
    const items = readLocalSponsoredContent();
    const next = { ...normalized, createdAt: item.createdAt ?? new Date(), updatedAt: new Date() };
    writeLocalSponsoredContent(items.some((entry) => entry.id === normalized.id) ? items.map((entry) => entry.id === normalized.id ? next : entry) : [...items, next]);
    return;
  }
  requireFirebaseUser(session);
  const existing = Boolean(item.createdAt);
  await setDoc(doc(getFirebaseServices()!.db, "sponsoredStoreContent", normalized.id), { ...normalized, ...(existing ? {} : { createdAt: serverTimestamp() }), updatedAt: serverTimestamp() }, { merge: existing });
}

export async function deleteSponsoredStoreContent(session: TerraBoaSession, contentId: string) {
  if (!session.isAdmin) throw new Error("admin-required");
  if (session.mode === "local") { writeLocalSponsoredContent(readLocalSponsoredContent().filter((item) => item.id !== contentId)); return; }
  requireFirebaseUser(session);
  await deleteDoc(doc(getFirebaseServices()!.db, "sponsoredStoreContent", contentId));
}

export async function savePromotionCampaign(session: TerraBoaSession, campaign: PromotionCampaign) {
  if (!session.isAdmin) throw new Error("admin-required");
  const normalized: PromotionCampaign = {
    ...campaign,
    id: campaign.id.trim().slice(0, 100),
    storeId: campaign.storeId.trim().slice(0, 100),
    title: campaign.title.trim().replace(/\s+/g, " ").slice(0, 120),
    message: campaign.message.trim().replace(/\s+/g, " ").slice(0, 500),
    category: campaign.category.trim().replace(/\s+/g, " ").slice(0, 80),
    state: campaign.state.trim().toUpperCase().slice(0, 2),
    ...(campaign.municipality?.trim() ? { municipality: campaign.municipality.trim().replace(/\s+/g, " ").slice(0, 80) } : {}),
    startsOn: campaign.startsOn,
    endsOn: campaign.endsOn,
    active: campaign.active === true,
    type: campaign.type ?? "opt-in",
    ...(campaign.coverImage ? { coverImage: campaign.coverImage } : {}),
    ...(campaign.ownerProfileId ? { ownerProfileId: campaign.ownerProfileId } : {}),
  };
  if (!normalized.id || !normalized.storeId || normalized.title.length < 3 || normalized.message.length < 5 || normalized.category.length < 2 || !/^(AC|AL|AP|AM|BA|CE|DF|ES|GO|MA|MT|MS|MG|PA|PB|PR|PE|PI|RJ|RN|RS|RO|RR|SC|SP|SE|TO)$/.test(normalized.state) || !/^\d{4}-\d{2}-\d{2}$/.test(normalized.startsOn) || !/^\d{4}-\d{2}-\d{2}$/.test(normalized.endsOn) || normalized.endsOn < normalized.startsOn) throw new Error("invalid-promotion-campaign");
  if (session.mode === "local") {
    const items = readLocalPromotionCampaigns();
    const next = { ...normalized, createdAt: campaign.createdAt ?? new Date(), updatedAt: new Date() };
    writeLocalPromotionCampaigns(items.some((item) => item.id === normalized.id) ? items.map((item) => item.id === normalized.id ? next : item) : [...items, next]);
    return;
  }
  requireFirebaseUser(session);
  const existing = Boolean(campaign.createdAt);
  await setDoc(doc(getFirebaseServices()!.db, "promotionCampaigns", normalized.id), { ...normalized, ...(existing ? {} : { createdAt: serverTimestamp() }), updatedAt: serverTimestamp() }, { merge: existing });
}

export async function saveOwnStoreDetails(session: TerraBoaSession, storeId: string, details: Pick<SupplyStore, "name" | "municipality" | "state" | "whatsapp" | "address" | "businessHours" | "locationUrl" | "branches"> & { municipalityCode?: string; activityType?: string }) {
  if (session.mode === "local") {
    writeLocalSupplyStores(readLocalSupplyStores().map((item) => item.id === storeId ? { ...item, ...details } : item));
    return;
  }
  requireFirebaseUser(session);
  const callable = httpsCallable(getFirebaseServices()!.functions, "saveTerraBoaStoreProfile");
  await callable({ storeId, ...details });
}

export async function saveOwnStorePhoto(session: TerraBoaSession, storeId: string, avatar: MediaImage) {
  if (session.mode === "local") return saveProfileMediaRecord(session, session.profileId, avatar);
  requireFirebaseUser(session);
  const callable = httpsCallable(getFirebaseServices()!.functions, "saveTerraBoaStorePhoto");
  await callable({ storeId, avatar });
}

export async function saveOwnStoreCampaign(session: TerraBoaSession, storeId: string, campaign: PromotionCampaign) {
  if (session.mode === "local") {
    const items = readLocalPromotionCampaigns();
    writeLocalPromotionCampaigns(items.some((item) => item.id === campaign.id) ? items.map((item) => item.id === campaign.id ? campaign : item) : [...items, campaign]);
    return;
  }
  requireFirebaseUser(session);
  const callable = httpsCallable(getFirebaseServices()!.functions, "saveTerraBoaStoreCampaign");
  await callable({ storeId, campaign });
}

export async function deleteOwnStoreCampaign(session: TerraBoaSession, storeId: string, campaignId: string) {
  if (session.mode === "local") { writeLocalPromotionCampaigns(readLocalPromotionCampaigns().filter((item) => item.id !== campaignId)); return; }
  requireFirebaseUser(session);
  const callable = httpsCallable(getFirebaseServices()!.functions, "deleteTerraBoaStoreCampaign");
  await callable({ storeId, campaignId });
}

export async function deletePromotionCampaign(session: TerraBoaSession, campaignId: string) {
  if (!session.isAdmin) throw new Error("admin-required");
  if (session.mode === "local") { writeLocalPromotionCampaigns(readLocalPromotionCampaigns().filter((item) => item.id !== campaignId)); return; }
  requireFirebaseUser(session);
  await deleteDoc(doc(getFirebaseServices()!.db, "promotionCampaigns", campaignId));
}

export async function savePromotionPreference(session: TerraBoaSession, preference: PromotionPreference) {
  if (session.mode !== "local" && !session.uid) throw new Error("authentication-required");
  const normalized: PromotionPreference = {
    storeId: preference.storeId.trim().slice(0, 100),
    ownerUid: session.uid ?? session.profileId,
    enabled: preference.enabled === true,
    categories: [...new Set(preference.categories.map((item) => item.trim().replace(/\s+/g, " ").slice(0, 80)).filter(Boolean))].slice(0, 20),
    state: preference.state.trim().toUpperCase().slice(0, 2),
    ...(preference.municipality?.trim() ? { municipality: preference.municipality.trim().replace(/\s+/g, " ").slice(0, 80) } : {}),
  };
  if (!normalized.storeId || !normalized.categories.length || !/^(AC|AL|AP|AM|BA|CE|DF|ES|GO|MA|MT|MS|MG|PA|PB|PR|PE|PI|RJ|RN|RS|RO|RR|SC|SP|SE|TO)$/.test(normalized.state)) throw new Error("invalid-promotion-preference");
  if (session.mode === "local") {
    const items = readLocalPromotionPreferences(session.profileId);
    const next = { ...normalized, updatedAt: new Date() };
    writeLocalPromotionPreferences(session.profileId, items.some((item) => item.storeId === normalized.storeId) ? items.map((item) => item.storeId === normalized.storeId ? next : item) : [...items, next]);
    return;
  }
  requireFirebaseUser(session);
  await setDoc(doc(getFirebaseServices()!.db, "users", session.uid, "promotionPreferences", normalized.storeId), { ...normalized, updatedAt: serverTimestamp() });
}

function sealSlug(value: string) {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 70);
}

export async function saveCertificationSeal(session: TerraBoaSession, seal: CertificationSeal) {
  if (!session.isAdmin) throw new Error("admin-required");
  const normalized: CertificationSeal = {
    ...seal,
    id: seal.id || `seal-${sealSlug(seal.name)}-${Date.now().toString(36)}`,
    name: seal.name.trim().replace(/\s+/g, " ").slice(0, 60),
    description: seal.description.trim().replace(/\s+/g, " ").slice(0, 300),
    applicableRoles: [...new Set(seal.applicableRoles)],
  };
  if (normalized.name.length < 2 || normalized.description.length < 5 || !normalized.applicableRoles.length) throw new Error("invalid-seal");
  if (session.mode === "local") {
    let current: CertificationSeal[] = [];
    try { current = JSON.parse(localStorage.getItem(CERTIFICATION_SEALS_KEY) ?? "[]") as CertificationSeal[]; } catch { current = []; }
    localStorage.setItem(CERTIFICATION_SEALS_KEY, JSON.stringify([normalized, ...current.filter((item) => item.id !== normalized.id)]));
    return;
  }
  requireFirebaseUser(session);
  const reference = doc(getFirebaseServices()!.db, "certificationSeals", normalized.id);
  const snapshot = await getDoc(reference);
  await setDoc(reference, { ...normalized, ...(snapshot.exists() ? {} : { createdAt: serverTimestamp() }), updatedAt: serverTimestamp() }, { merge: true });
}

export async function deleteCertificationSeal(session: TerraBoaSession, sealId: string) {
  if (!session.isAdmin) throw new Error("admin-required");
  if (session.mode === "local") {
    let current: CertificationSeal[] = [];
    let assignments: ProfileSealAssignment[] = [];
    try { current = JSON.parse(localStorage.getItem(CERTIFICATION_SEALS_KEY) ?? "[]") as CertificationSeal[]; } catch { current = []; }
    try { assignments = JSON.parse(localStorage.getItem(PROFILE_SEAL_ASSIGNMENTS_KEY) ?? "[]") as ProfileSealAssignment[]; } catch { assignments = []; }
    localStorage.setItem(CERTIFICATION_SEALS_KEY, JSON.stringify(current.filter((item) => item.id !== sealId)));
    localStorage.setItem(PROFILE_SEAL_ASSIGNMENTS_KEY, JSON.stringify(assignments.map((item) => ({ ...item, sealIds: item.sealIds.filter((id) => id !== sealId) }))));
    return;
  }
  requireFirebaseUser(session);
  await deleteDoc(doc(getFirebaseServices()!.db, "certificationSeals", sealId));
}

export async function setProfileSealAssignment(session: TerraBoaSession, profileId: string, sealIds: string[]) {
  if (!session.isAdmin) throw new Error("admin-required");
  const normalizedIds = [...new Set(sealIds)].slice(0, 20);
  if (session.mode === "local") {
    let current: ProfileSealAssignment[] = [];
    try { current = JSON.parse(localStorage.getItem(PROFILE_SEAL_ASSIGNMENTS_KEY) ?? "[]") as ProfileSealAssignment[]; } catch { current = []; }
    localStorage.setItem(PROFILE_SEAL_ASSIGNMENTS_KEY, JSON.stringify([{ profileId, sealIds: normalizedIds }, ...current.filter((item) => item.profileId !== profileId)]));
    return;
  }
  requireFirebaseUser(session);
  const services = getFirebaseServices()!;
  const assignmentRef = doc(services.db, "profileSealAssignments", profileId);
  const eventRef = doc(collection(services.db, "sealAuditEvents"));
  const batch = writeBatch(services.db);
  batch.set(assignmentRef, { profileId, sealIds: normalizedIds, lastAuditEventId: eventRef.id, updatedAt: serverTimestamp(), updatedBy: session.uid });
  batch.set(eventRef, { id: eventRef.id, profileId, sealIds: normalizedIds, actorUid: session.uid, actorEmail: session.email ?? "administracao@terraboa", createdAt: serverTimestamp() });
  await batch.commit();
}

export async function deleteListingRecord(session: TerraBoaSession, listingId: string) {
  if (session.mode === "local") {
    writeLocalListings(readLocalListings().filter((item) => item.id !== listingId));
    return;
  }

  requireFirebaseUser(session);
  const services = getFirebaseServices()!;
  await deleteDoc(doc(services.db, "listings", listingId));
}

export async function setFavoriteRecord(session: TerraBoaSession, profileId: string, listingId: string, favorite: boolean) {
  if (session.mode === "local") {
    const next = readLocalFavorites(profileId);
    if (favorite) next.add(listingId);
    else next.delete(listingId);
    localStorage.setItem(favoritesKey(profileId), JSON.stringify([...next]));
    return;
  }

  requireFirebaseUser(session);
  const services = getFirebaseServices()!;
  const favoriteRef = doc(services.db, "users", session.uid, "favorites", listingId);
  if (favorite) {
    await setDoc(favoriteRef, { listingId, profileId, createdAt: serverTimestamp() });
  } else {
    await deleteDoc(favoriteRef);
  }
}

export async function sendResponseRecord(
  session: TerraBoaSession,
  listing: Listing,
  message: string,
  offer: string,
) {
  if (session.mode === "local") {
    const next = readLocalListings().map((item) =>
      item.id === listing.id ? { ...item, responses: item.responses + 1 } : item,
    );
    writeLocalListings(next);
    return;
  }

  requireFirebaseUser(session);
  const services = getFirebaseServices()!;
  const listingRef = doc(services.db, "listings", listing.id);
  const responseRef = doc(collection(services.db, "responses"));

  await runTransaction(services.db, async (transaction) => {
    const snapshot = await transaction.get(listingRef);
    if (!snapshot.exists()) throw new Error("listing-not-found");
    const current = snapshot.data() as Listing;
    transaction.set(responseRef, {
      listingId: listing.id,
      module: listing.module as ModuleKey,
      responderId: session.profileId,
      message,
      offer: offer ? Number(offer) : null,
      createdAt: serverTimestamp(),
    });
    transaction.update(listingRef, { responses: Number(current.responses ?? 0) + 1 });
  });
}
